import React, { useState, useEffect, useCallback } from 'react'
import { BackHandler } from 'react-native'
import { useSelector } from 'APP/Hooks/redux'
import { useNavigationBuilder, createNavigatorFactory, StackRouter } from '@react-navigation/native'
import styled from 'styled-components/native'
import { NativeStackView } from '@react-navigation/native-stack'
import { StackView } from '@react-navigation/stack'

// Services, Hooks, & Helpers
import useScreenView from 'APP/Hooks/useScreenView'
import { useStackRoutes } from 'APP/Nav/useStackRoutes'
import {
  selectDescriptorOfCurrentRoute,
  routesToStackSideMenus,
  isAtFirstRoute,
} from 'APP/Nav/Utils'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import { popToNative } from 'APP/BrownfieldBridge/BrownfieldBridgeInternal'
import { isAndroid } from 'APP/Helpers/checkPlatform'

import Config from 'APP/Config'

// Components
import { DebugCrumbs } from 'APP/Nav/Components/DebugCrumbs'
import { PopToNativeBanner } from 'APP/Nav/Components/PopToNativeBanner'
import { OnboardingExitModal } from 'APP/Components/OnboardingExitModal'

const Container = styled.View`
  width: 100%;
  height: 100%;
  flex-direction: row;
`

const StackContainer = styled.View`
  flex: 1;
`

const StackMenuContainer = styled.View`
  background-color: ${({ theme }) => theme.colors.elementsBg};
  width: 240px;
  z-index: 2;
  ${({ theme }) => theme.shadows.normal};
`

const CustomStackNavigator = ({
  initialRouteName,
  backBehavior,
  children,
  screenOptions,
  ...rest
}) => {
  const { NavigationContent, descriptors, state, navigation } = useNavigationBuilder(StackRouter, {
    initialRouteName,
    backBehavior,
    children,
    screenOptions,
  })

  /*
    Note:
    We use stack routes from the root state instead of the stack navigator supplied
    state, as it includes sub-navigators (so we can see what's up with the tab navigator
    inside of the screen)
  */
  const stackRoutes = useStackRoutes()
  const { isSmallScreen } = useScreenView()
  const { options } = selectDescriptorOfCurrentRoute(descriptors, stackRoutes) || {}
  const { stackMenuStyle } = options || {}
  const stackSideMenus = routesToStackSideMenus(stackRoutes)
  const showDebugCrumbs = useSelector((state) => state?.app?.showDebugCrumbs)
  const isOnboarding = useSelector((state) => state?.patient?.isOnboarding)
  const showStackMenu = !isSmallScreen && stackSideMenus.length !== 0

  const [showExitModal, setShowExitModal] = useState(false)

  useEffect(() => {
    if (!isAndroid()) return

    const backHandlerSubscription = BackHandler.addEventListener('hardwareBackPress', () => {
      // Evaluated lazily so navigationRef has had a chance to attach.
      if (!isAtFirstRoute(navigation)) return false

      if (isEmbeddedEnvironment()) {
        popToNative(true)
        return true
      }

      if (isOnboarding) {
        setShowExitModal(true)
        return true
      }

      return false
    })
    return () => backHandlerSubscription.remove()
  }, [navigation, isOnboarding])

  const handleQuit = useCallback(() => {
    setShowExitModal(false)
    BackHandler.exitApp()
  }, [])

  const handleCancel = useCallback(() => setShowExitModal(false), [])

  /*
    Note:
    In order to force iOS style modals on android, we can't use the NativeStack.
    This will impact performance, we might want to consider using native modals
    on android if performance is an issue.
  */
  const NavigationStackView = isAndroid() ? StackView : NativeStackView

  return (
    <Container>
      {showStackMenu && (
        <StackMenuContainer testID="STACK_MENU_CONTAINER" style={stackMenuStyle}>
          {stackSideMenus}
        </StackMenuContainer>
      )}
      <StackContainer>
        {isEmbeddedEnvironment() && Config.EMBEDDED_DISPLAY_POP_TO_NATIVE_BANNER && (
          <PopToNativeBanner />
        )}
        {showDebugCrumbs && <DebugCrumbs {...{ descriptors, stackRoutes }} />}
        <NavigationContent>
          <NavigationStackView {...rest} {...{ descriptors, state, navigation }} />
        </NavigationContent>
      </StackContainer>
      <OnboardingExitModal visible={showExitModal} onQuit={handleQuit} onCancel={handleCancel} />
    </Container>
  )
}

export const createCustomStackNavigator = createNavigatorFactory(CustomStackNavigator)
